import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FaAddressBook } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import classNames from 'classnames';
import validator from 'email-validator';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import connectionService from '../../../services/community/connectionService';
import localContactService from '../../../services/community/localContactService';
import { Label, ProfileDataType } from '../../../types/Profile';
import SearchBar from '../forms/SearchBar';
import Input from '../misc/Input';
import ModalLayout from '../layouts/ModalLayout';
import ConnectionsList from '../misc/ConnectionsList';
import CancelSaveButton from '../misc/CancelSaveButton';
import ModalHeader from '../misc/ModalHeader';

export interface AddExternalMemberToCommunityModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** The dashboard id of the subcommunity to add the external members to. */
  communityDashboardId: number;
  /** The current members of the subcommunity, used to exclude already linked external members. */
  existingMembers: Connection[];
  onMembersAdded?: () => void;
}

type Tab = 'LINK' | 'CREATE';

function isExternalMember(connection: Connection): connection is Connection & { externalMemberId: number } {
  return connection.type === ConnectionType.EXTERNAL_MEMBER && connection.externalMemberId != null;
}

export default function AddExternalMemberToCommunityModal({
  open,
  setOpen,
  communityDashboardId,
  existingMembers,
  onMembersAdded,
}: AddExternalMemberToCommunityModalProps): JSX.Element {
  const { t } = useTranslation();
  const connections = useSelector((state: RootState) => state.connections);

  const [tab, setTab] = useState<Tab>('LINK');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const isSubmittingRef = useRef(false);

  // create-new form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [sendInvite, setSendInvite] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) {
      setTab('LINK');
      setSelectedIds([]);
      setSearch('');
      setFirstName('');
      setLastName('');
      setEmail('');
      setNote('');
      setSendInvite(false);
      setEmailError(undefined);
      return;
    }
    if (!connections.length) connectionService.getConnections();
  }, [open, connections.length]);

  // external members already present in the subcommunity: either linked (parentExternalMemberId
  // points at the business dashboard's root member) or the same external member row
  const alreadyLinkedRootIds = useMemo(
    () =>
      new Set(
        existingMembers.flatMap((member) =>
          member.parentExternalMemberId != null
            ? [member.parentExternalMemberId]
            : member.externalMemberId != null
              ? [member.externalMemberId]
              : [],
        ),
      ),
    [existingMembers],
  );

  const linkableConnections = useMemo(
    () =>
      connections
        .filter(isExternalMember)
        .filter((connection) => connection.parentExternalMemberId == null)
        .map((connection) => ({
          id: String(connection.externalMemberId),
          alias: `${connection.fields.FIRST_NAME}+${connection.fields.LAST_NAME}`,
          picture: connection.picture,
          disabled: alreadyLinkedRootIds.has(connection.externalMemberId),
        })),
    [connections, alreadyLinkedRootIds],
  );

  const handleLink = async (): Promise<void> => {
    if (!selectedIds.length) {
      toast.error(t('component.modal.addExternalMemberToCommunity.error.noSelected'));
      return;
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setLoading(true);

    try {
      await localContactService.linkExternalMembers(
        communityDashboardId,
        selectedIds.map((id) => parseInt(id, 10)),
      );
      toast.success(
        t('component.modal.addExternalMemberToCommunity.success.linked', {
          count: selectedIds.length,
        }),
      );
      onMembersAdded?.();
      setOpen(false);
    } catch (error) {
      console.error('Failed to link external members:', error);
      const apiMessage = axios.isAxiosError(error)
        ? (error.response?.data?.message as string | undefined)
        : undefined;
      toast.error(apiMessage || t('component.modal.addExternalMemberToCommunity.error.failed'));
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleCreate = async (): Promise<void> => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error(t('component.modal.addExternalMemberToCommunity.error.nameRequired'));
      return;
    }
    if (email && !validator.validate(email.trim())) {
      setEmailError(t('component.modal.addExternalMemberToCommunity.error.invalidEmail'));
      return;
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setLoading(true);
    setEmailError(undefined);

    try {
      const [created] = await localContactService.createLocalContacts(
        [
          {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            note,
            customFieldResponses: {},
            receivedData: email
              ? [
                  {
                    dataType: ProfileDataType.EMAIL,
                    label: Label.GENERAL,
                    description: '',
                    email: email.trim(),
                  },
                ]
              : [],
          },
        ],
        communityDashboardId,
      );

      if (sendInvite && email && created?.externalMemberId) {
        await localContactService.inviteContacts([created.externalMemberId], communityDashboardId);
      }

      toast.success(t('component.modal.addExternalMemberToCommunity.success.created'));
      onMembersAdded?.();
      setOpen(false);
    } catch (error) {
      console.error('Failed to create external member:', error);
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const backendMessage: string | undefined =
          error.response?.data?.message || error.response?.data?.error;
        if (backendMessage?.toLowerCase().includes('already exists in this dashboard')) {
          setEmailError(t('component.modal.localContact.emailAlreadyExists'));
          setLoading(false);
          isSubmittingRef.current = false;
          return;
        }
      }
      toast.error(t('component.modal.addExternalMemberToCommunity.error.failed'));
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const tabButtonClass = (active: boolean): string =>
    classNames(
      'flex-1 rounded-full px-4 py-2 text-sm font-semibold transition-colors',
      active ? 'bg-primary-300 text-secondary-50' : 'bg-secondary-200 text-gray-700',
    );

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton={!loading}
      className="z-50 mx-6 flex w-fit min-w-[450px] max-w-[650px] flex-col gap-[20px] rounded-[12px] bg-secondary-50 px-10 py-6 pb-4 pt-5">
      <ModalHeader
        icon={FaAddressBook}
        title={t('component.modal.addExternalMemberToCommunity.title')}
        subtitle={t('component.modal.addExternalMemberToCommunity.subtitle')}
      />

      <div className="w-full border-b border-primary-300" />

      <div className="flex w-full gap-2">
        <button type="button" className={tabButtonClass(tab === 'LINK')} onClick={() => setTab('LINK')}>
          {t('component.modal.addExternalMemberToCommunity.tabLink')}
        </button>
        <button type="button" className={tabButtonClass(tab === 'CREATE')} onClick={() => setTab('CREATE')}>
          {t('component.modal.addExternalMemberToCommunity.tabCreate')}
        </button>
      </div>

      {tab === 'LINK' ? (
        <>
          <p className="text-sm text-gray-600">
            {t('component.modal.addExternalMemberToCommunity.linkExplanation')}
          </p>

          <SearchBar search={search} setSearch={setSearch} />

          <ConnectionsList
            selected={selectedIds}
            setSelected={setSelectedIds}
            hasSearch={false}
            className="border-none py-0"
            search={search}
            includePicture
            connections={linkableConnections}
            disabledMessage={t('component.modal.addExternalMemberToCommunity.alreadyAdded')}
          />

          <div className="w-full border-b border-primary-300" />

          <div className="flex flex-col items-end gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p>
              {selectedIds.length} {t('component.modal.addExternalMemberToCommunity.selected')}
            </p>
            <CancelSaveButton
              buttonText={t('component.modal.addExternalMemberToCommunity.linkButton')}
              onCancel={() => setOpen(false)}
              disabled={selectedIds.length === 0 || loading}
              loading={loading}
              onSave={handleLink}
            />
          </div>
        </>
      ) : (
        <>
          <div className="flex w-full flex-col gap-4">
            <div className="flex gap-4">
              <Input
                id="external-member-first-name"
                label={t('component.modal.addExternalMemberToCommunity.firstName')}
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-lg border px-3 py-2"
              />
              <Input
                id="external-member-last-name"
                label={t('component.modal.addExternalMemberToCommunity.lastName')}
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full rounded-lg border px-3 py-2"
              />
            </div>
            <Input
              id="external-member-email"
              label={t('component.modal.addExternalMemberToCommunity.email')}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(undefined);
              }}
              className="w-full rounded-lg border px-3 py-2"
            />
            {emailError && <p className="text-sm text-danger">{emailError}</p>}
            {email && validator.validate(email) && (
              <div className="flex items-center gap-2 pl-1">
                <input
                  type="checkbox"
                  id="external-member-send-invite"
                  checked={sendInvite}
                  onChange={(e) => setSendInvite(e.target.checked)}
                  className="accent-primary-500"
                />
                <label htmlFor="external-member-send-invite" className="text-sm">
                  {t('component.modal.localContact.sendInvite')}
                </label>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">
                {t('component.modal.addExternalMemberToCommunity.note')}
              </p>
              <textarea
                className="h-24 w-full resize-none rounded-lg placeholder-secondary-200"
                onChange={(e) => setNote(e.target.value)}
                value={note}
              />
            </div>
          </div>

          <div className="w-full border-b border-primary-300" />

          <div className="flex justify-end">
            <CancelSaveButton
              buttonText={t('component.modal.addExternalMemberToCommunity.createButton')}
              onCancel={() => setOpen(false)}
              disabled={!firstName.trim() || !lastName.trim() || loading}
              loading={loading}
              onSave={handleCreate}
            />
          </div>
        </>
      )}
    </ModalLayout>
  );
}
